<template>
    <div class="product-details-content">
        <el-affix :z-index="maxZIndex">
            <NavBar></NavBar>
        </el-affix>

        <div class="main">
            <Breadcrumb :bread-list="breadList" />

            <div class="top">
                <div class="product-details-box pc">
                    <div class="left">
                        <div class="swiper-vertical-box">
                            <!-- 上箭头 -->
                            <div ref="prevRef" class="swiper-button-prev-custom pointer-cursor ">
                                <el-icon size="30">
                                    <ArrowUp />
                                </el-icon>
                            </div>

                            <!-- Swiper 主体 -->
                            <Swiper direction="vertical" :slides-per-view="4" loop :modules="[Navigation]"
                                :pagination="{ clickable: true, el: '.swiper-pagination' }"
                                :navigation="{ prevEl: prevRef, nextEl: nextRef }" class="swiper-content-pc">
                                <SwiperSlide v-for="(img, index) in details.images" :key="index" class="swiper-slide">
                                    <div class="swiper-item-pc pointer-cursor"
                                        @click="handleClickSmallImages(img.path)">
                                        <img :src="`${img.path}`" class="slide-img" />
                                    </div>
                                </SwiperSlide>
                                <div class="swiper-pagination"></div>
                            </Swiper>

                            <!-- 下箭头 -->
                            <div ref="nextRef" class="swiper-button-next-custom pointer-cursor ">
                                <el-icon size="30">
                                    <ArrowDown />
                                </el-icon>
                            </div>
                        </div>
                    </div>
                    <div class="middle">
                        <div class="" ref="middleRef">
                            <el-image style="width: 500px; height: 500px"
                                :src="`${details.cover}`" ref="mainImageRef"
                                @mousemove="handleMouseMove" @mouseenter="handleMouseEnter"
                                @mouseleave="handleMouseLeave" />
                        </div>
                        <!-- 放大镜镜头 -->
                        <div v-if="showZoom" class="zoom-lens" :style="lensStyle"></div>

                        <!-- 放大镜大图显示 -->
                        <div v-if="showZoom" class="zoom-result" :style="zoomStyle"></div>
                    </div>

                    <div class="right">
                        <div class="title">{{ details.name }}</div>

                        <div class="describe">{{ details.describeText }}</div>
                    </div>
                </div>

                <div class="phone">
                    <Swiper loop :modules="[Navigation, Pagination]"
                        :pagination="{ clickable: true, el: '.swiper-pagination' }"
                        :navigation="{ prevEl: prevRef, nextEl: nextRef }" class="swiper-content-phone">
                        <SwiperSlide v-for="(img, index) in details.images" :key="index" class="swiper-slide">
                            <div class="swiper-item-phone pointer-cursor" @click="handleClickSmallImages(img.path)">
                                <img :src="`${img.path}`" class="slide-img" />
                            </div>
                        </SwiperSlide>
                        <div class="swiper-pagination"></div>
                    </Swiper>

                    <div class="right">
                        <div class="title">{{ details.name }}</div>

                        <div class="describe">{{ details.describeText }}</div>
                    </div>
                </div>
            </div>

            <div class="bottom">
                <div class="title">{{ $t('products.describe') }}</div>

                <div class="content" v-html="details.content"></div>
            </div>
        </div>

        <Footer></Footer>
    </div>
</template>

<script lang="ts" setup>
import { useRoute } from 'vue-router'
import NavBar from '@/components/NavBar.vue'
import Footer from '@/components/Footer.vue'
import { UtilMethods } from '@/util/UtilMethods'
import Breadcrumb from '@/components/Breadcrumb.vue'
import { BreadType } from '@/typeDefine/EntityType'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Pagination } from 'swiper/modules'
import { Ref, ref, reactive, computed, onMounted } from 'vue'
import { ArrowUp, ArrowDown } from '@element-plus/icons-vue'
import { Action } from '@/typeDefine/CommonTypes'
import { Api } from '@/http/Api'
import { GetProductDetailsByIdResponse } from '@/typeDefine/HttpResponseType'
import { ResponseCode } from '@/http/ResponseCode'
import { ConstantField } from '@/constant/ConstantField'

const maxZIndex: string = UtilMethods.getDOMStyleFromCSS('--max-z-index')
const route = useRoute()
const prevRef = ref<HTMLElement | null>(null)
const nextRef = ref<HTMLElement | null>(null)
const middleRef = ref<HTMLElement | null>(null)
const mainImageRef = ref<HTMLElement | null>(null)
const showZoom = ref(false)
// 镜头位置
const lensPos = reactive({ x: 0, y: 0 })

// 镜头尺寸
const lensWidth = 150
const lensHeight = 150

// 放大倍数
const zoomScale = 3

let animationFrameId: number | null = null

const breadList: BreadType[] = [
    {
        to: '/products',
        text: 'index.navBar.product'
    },
    {
        text: 'index.navBar.productDetails'
    }
]

const details: Ref<GetProductDetailsByIdResponse['data']['details']> = ref({
    name: '',
    typeId: 0,
    id: 0,
    images: [],
    content: '',
    cover: '',
    describeText: '',
    hot: 0,
})

onMounted(() => {
    if (route.params.id && typeof route.params.id === 'string') {
        requestProductDetails(Number.parseInt(route.params.id))
    }
})

const requestProductDetails: Action<[number]> = async (id: number) => {
    const resonse: GetProductDetailsByIdResponse = await Api.instance.getProductDetailsById({
        id
    })

    UtilMethods.showMessageToast(resonse, false)

    if (resonse.code === ResponseCode.OK) {
        details.value = resonse.data.details

        details.value.images.unshift({
            id: 0,
            path: details.value.cover,
            productId: 0
        })
    }
}

// 计算镜头样式
const lensStyle = computed(() => ({
    width: `${lensWidth}px`,
    height: `${lensHeight}px`,
    top: `${lensPos.y}px`,
    left: `${lensPos.x}px`,
}))

// 计算放大镜显示样式
const zoomStyle = computed(() => ({
    width: `${lensWidth * zoomScale}px`,
    height: `${lensHeight * zoomScale}px`,
    backgroundImage: `url(${details.value.cover})`,
    backgroundRepeat: 'no-repeat',
    // 背景图放大尺寸
    backgroundSize: `${500 * zoomScale}px ${500 * zoomScale}px`,
    // 背景图位置（反向移动镜头位置）
    backgroundPosition: `-${lensPos.x * zoomScale}px -${lensPos.y * zoomScale}px`,
}))

// 鼠标进入显示放大镜
function handleMouseEnter() {
    showZoom.value = true
}

// 鼠标离开隐藏放大镜
function handleMouseLeave() {
    showZoom.value = false
}

// 鼠标移动更新镜头位置
function handleMouseMove(event: MouseEvent) {
    if (!middleRef.value) return

    if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
    }

    animationFrameId = requestAnimationFrame(() => {
        const rect = middleRef.value!.getBoundingClientRect()

        let x = event.clientX - rect.left - lensWidth / 2
        let y = event.clientY - rect.top - lensHeight / 2

        x = Math.max(0, Math.min(x, rect.width - lensWidth))
        y = Math.max(0, Math.min(y, rect.height - lensHeight))

        lensPos.x = x
        lensPos.y = y
    })
}

const handleClickSmallImages: Action<[string]> = (src) => {
    details.value.cover = `${src}`
}
</script>

<style scoped>
.product-details-content .main {
    width: 90%;
    margin: 50px auto;
    min-height: calc(100vh - 70px - 300px);
}

.product-details-content .main .top {
    margin-bottom: 50px;
}

.product-details-content .main .top .phone {
    display: none;
}

.product-details-content .main .product-details-box {
    display: flex;
}

.product-details-content .main .top .phone .swiper-content-phone {
    height: 300px;
}

.product-details-content .main .top .phone .swiper-slide {
    height: 100%;
}

.product-details-content .main .top .phone .swiper-item-phone {
    height: 100%;
}

.product-details-content .main .top .phone .right .title {
    font-size: 22px;
    margin: 10px 0;
}

.product-details-content .main .product-details-box .left {
    width: 10%;
}

.product-details-content .main .product-details-box .middle {
    margin-left: 40px;
    width: 500px;
    height: 400px;
}

.product-details-content .main .product-details-box .right {
    flex: 1;
    margin-left: 50px;
}

.product-details-content .main .product-details-box .right .title {
    font-size: 22px;
    margin-bottom: 10px;
}

.product-details-content .main .product-details-box .right .describe {
    font-size: 14px;
    color: #888;
    line-height: 1.9;
}

.product-details-content .main .bottom .title {
    font-size: 30px;
    color: #222;
    padding-left: 28px;
    border-left: 5px solid #feb900;
    margin-bottom: 20px;
}

/* Swiper 总容器 */
.swiper-vertical-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
}

/* Swiper 内容区 */
.swiper-content-pc {
    height: 400px;
    width: 88px;
    position: relative;
}

/* 每项样式 */
.swiper-item-pc {
    width: auto;
    height: auto;
}

.slide-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid #eee;
}

.middle {
    position: relative;
}

/* 镜头框 */
.zoom-lens {
    position: absolute;
    border: 2px solid #aaa;
    background-color: rgba(255, 255, 255, 0.3);
    pointer-events: none;
    /* 让镜头不挡鼠标 */
    box-sizing: border-box;
    border-radius: 4px;
    transition: background-color 0.2s ease;
}

/* 放大图显示容器 */
.zoom-result {
    position: absolute;
    top: 0;
    left: 520px;
    /* 大图宽度500 + 20px间距 */
    border: 1px solid #ccc;
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.15);
    background-repeat: no-repeat;
    background-position: center;
    border-radius: 6px;
    z-index: 1000;
    /* 使动画更丝滑 */
    transition: background-position 0.1s ease;
}

@media (max-width: 1200px) {
    .product-details-content .main .top .phone {
        display: block;
    }

    .product-details-content .main .top .pc {
        display: none;
    }

    .product-details-content .main .top .phone .swiper-content-phone {
        height: 600px;
    }

    .product-details-content .main .top .phone .right .describe {
        font-size: 16px;
    }
}

@media (max-width: 768px) {
    .product-details-content .main .top .phone .swiper-content-phone {
        height: 400px;
    }

    .product-details-content .main .bottom .title {
        font-size: 24px;
    }
}

@media (max-width: 500px) {
    .product-details-content .main .top .phone .swiper-content-phone {
        height: 300px;
    }
}

@media (max-width: 400px) {
    .product-details-content .main .top .phone .swiper-content-phone {
        height: 250px;
    }
}
</style>
